import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { MUTATION_KEY, QUERY_KEY } from './keys';
import { queryClient } from './queryClient';
import { clmApiClient } from '../clients';
import type {
  ApiErrorResponse,
  DepartmentCreateRequest,
  DepartmentCreateRequestBody,
  DepartmentDetails,
} from '../types';
import { isApiErrorResponse } from '../utils';

const createDepartment = async (
  data: DepartmentCreateRequest,
): Promise<DepartmentDetails> => {
  const idnId = data.idnId;

  if (!idnId) {
    throw new Error('IDN ID is required to create a department.');
  }

  const response = await clmApiClient.post<DepartmentDetails, ApiErrorResponse>(
    'department',
    {
      department: data,
    } as DepartmentCreateRequestBody,
  );

  if (!response.data) {
    throw new Error('No data returned from create department');
  }

  if (!response.ok) {
    if (isApiErrorResponse(response.data)) {
      throw new Error(response.data.details);
    }
    throw new Error('Unknown error');
  }

  await queryClient.invalidateQueries({
    predicate: (query) => {
      const [key] = query.queryKey;

      return (
        key === QUERY_KEY.DEPARTMENT_SEARCH || key === QUERY_KEY.DEPARTMENTS_ALL
      );
    },
  });

  return response.data;
};

export const useDepartmentCreateMutation = (
  config?: Omit<
    UseMutationOptions<DepartmentDetails, Error, DepartmentCreateRequest>,
    'mutationFn'
  >,
) =>
  useMutation({
    mutationKey: [MUTATION_KEY.DEPARTMENT_CREATE],
    mutationFn: createDepartment,
    ...config,
  });
